import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import toast from "react-hot-toast";
import ButtonLoader from "../ButtonLoader";
import LoadingComponent from "../ComponentLoader";
import {
  createAuditRule,
  deactivateAuditRule,
  getAuditRules,
  updateAuditRule,
} from "../../services/decorator";
import {
  buildDefaultFormState,
  buildFormStateFromRule,
  buildQuickTemplateDraft,
  COMING_SOON_TEMPLATES,
  DTF_PRICE_FIELD_OPTIONS,
  isDecoratorScoped,
  isInstanceScoped,
  parseIntegerOrNull,
  RULE_TYPE_OPTIONS,
  QUANTITY_TOLERANCE_OPTIONS,
  REQUIRED_CHARGE_TRIGGER_OPTIONS,
  SCOPE_OPTIONS,
  SEVERITY_OPTIONS,
  serializeAuditRulePayload,
  TEMPLATE_OPTIONS,
} from "./auditRuleTemplates.mjs";

const TAB_INDEX = {
  auditRules: 0,
  blackoutDays: 1,
  instanceOverrides: 2,
  previewAudit: 3,
};

const formatTemplateLabel = (templateKey) =>
  TEMPLATE_OPTIONS.find((option) => option.value === templateKey)?.label || templateKey;

const formatScopeLabel = (scopeType) =>
  SCOPE_OPTIONS.find((option) => option.value === scopeType)?.label || scopeType;

const PlaceholderTab = ({ message }) => (
  <Box
    sx={{
      minHeight: 260,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 3,
      py: 6,
      textAlign: "center",
      color: "#64748b",
    }}
  >
    <Typography variant="body1">{message}</Typography>
  </Box>
);

const RuleEditorDialog = ({
  open,
  onClose,
  onSave,
  saving,
  decoratorsOptions,
  formState,
  setFormState,
  currentInstanceId,
}) => {
  const setField = (field, value) =>
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));

  const scopeRequiresInstance = isInstanceScoped(formState.scopeType);
  const scopeRequiresDecorator = isDecoratorScoped(formState.scopeType);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">
          {formState.id ? "Edit Audit Rule" : "Create Audit Rule"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f8fafb" }}>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {formState.draftNotice ? (
            <Alert severity="info">
              {formState.draftNotice}
            </Alert>
          ) : null}
          <TextField
            label="Rule Name"
            value={formState.name}
            onChange={(event) => setField("name", event.target.value)}
            fullWidth
          />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                label="Template"
                value={formState.templateKey}
                onChange={(event) => setField("templateKey", event.target.value)}
              >
                {TEMPLATE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                label="Scope"
                value={formState.scopeType}
                onChange={(event) => setField("scopeType", event.target.value)}
              >
                {SCOPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Numeric Instance ID"
              type="number"
              fullWidth
              disabled={!scopeRequiresInstance}
              value={scopeRequiresInstance ? formState.instanceId : ""}
              helperText={
                scopeRequiresInstance
                  ? `Uses numeric Instances.id${currentInstanceId ? ` (current: ${currentInstanceId})` : ""}.`
                  : "Not required for the selected scope."
              }
              onChange={(event) => setField("instanceId", event.target.value)}
            />

            <FormControl fullWidth disabled={!scopeRequiresDecorator}>
              <InputLabel>Decorator</InputLabel>
              <Select
                label="Decorator"
                value={scopeRequiresDecorator ? formState.decoratorId : ""}
                onChange={(event) => setField("decoratorId", event.target.value)}
              >
                {decoratorsOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Applies To Method"
            value={formState.appliesToMethod}
            onChange={(event) => setField("appliesToMethod", event.target.value)}
            helperText="Optional. Example: Embroidery"
            fullWidth
          />

          {formState.templateKey === "required_charge" ? (
            <>
              <FormControl fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  label="Trigger Type"
                  value={formState.triggerType}
                  onChange={(event) => setField("triggerType", event.target.value)}
                >
                  {REQUIRED_CHARGE_TRIGGER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formState.triggerType !== "always" ? (
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField
                    label="Trigger Field"
                    value={formState.triggerField}
                    onChange={(event) => setField("triggerField", event.target.value)}
                    placeholder="product.meta.garmentType"
                    fullWidth
                  />
                  <TextField
                    label="Trigger Value"
                    value={formState.triggerValue}
                    onChange={(event) => setField("triggerValue", event.target.value)}
                    placeholder="hoodie"
                    fullWidth
                  />
                </Stack>
              ) : null}

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Required Charge ID"
                  value={formState.requiredChargeId}
                  onChange={(event) =>
                    setField("requiredChargeId", event.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Required Charge Name Fallback"
                  value={formState.requiredChargeName}
                  onChange={(event) =>
                    setField("requiredChargeName", event.target.value)
                  }
                  fullWidth
                />
              </Stack>
            </>
          ) : null}

          {formState.templateKey === "quantity_match" ? (
            <>
              <TextField
                label="Compare"
                value="Product row quantity vs decoration quantity"
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                helperText="Quantity Match compares each decoration row quantity to its parent product row quantity."
              />
              <FormControl fullWidth>
                <InputLabel>Tolerance</InputLabel>
                <Select
                  label="Tolerance"
                  value={formState.quantityTolerance}
                  onChange={(event) =>
                    setField("quantityTolerance", event.target.value)
                  }
                >
                  {QUANTITY_TOLERANCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : null}

          {formState.templateKey === "charge_behavior" ? (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Charge ID"
                  value={formState.behaviorChargeId}
                  onChange={(event) =>
                    setField("behaviorChargeId", event.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Charge Name Fallback"
                  value={formState.behaviorChargeName}
                  onChange={(event) =>
                    setField("behaviorChargeName", event.target.value)
                  }
                  fullWidth
                />
              </Stack>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                  Expected Behavior
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.fixedQuantityOne}
                        onChange={(event) =>
                          setField("fixedQuantityOne", event.target.checked)
                        }
                      />
                    }
                    label="Fixed Quantity One"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.runChargeMatchesProductQuantity}
                        onChange={(event) =>
                          setField(
                            "runChargeMatchesProductQuantity",
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Run Charge Matches Quantity"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.includeInPriceEnabled}
                        onChange={(event) =>
                          setField("includeInPriceEnabled", event.target.checked)
                        }
                      />
                    }
                    label="Check Include In Price"
                  />
                  {formState.includeInPriceEnabled ? (
                    <FormControlLabel
                      sx={{ ml: 3 }}
                      control={
                        <Switch
                          checked={formState.includeInPrice}
                          onChange={(event) =>
                            setField("includeInPrice", event.target.checked)
                          }
                        />
                      }
                      label={formState.includeInPrice ? "Expected: Yes" : "Expected: No"}
                    />
                  ) : null}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.displayToClientEnabled}
                        onChange={(event) =>
                          setField("displayToClientEnabled", event.target.checked)
                        }
                      />
                    }
                    label="Check Display To Client"
                  />
                  {formState.displayToClientEnabled ? (
                    <FormControlLabel
                      sx={{ ml: 3 }}
                      control={
                        <Switch
                          checked={formState.displayToClient}
                          onChange={(event) =>
                            setField("displayToClient", event.target.checked)
                          }
                        />
                      }
                      label={formState.displayToClient ? "Expected: Yes" : "Expected: No"}
                    />
                  ) : null}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.subtractFromMarginEnabled}
                        onChange={(event) =>
                          setField(
                            "subtractFromMarginEnabled",
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Check Subtract From Margin"
                  />
                  {formState.subtractFromMarginEnabled ? (
                    <FormControlLabel
                      sx={{ ml: 3 }}
                      control={
                        <Switch
                          checked={formState.subtractFromMargin}
                          onChange={(event) =>
                            setField("subtractFromMargin", event.target.checked)
                          }
                        />
                      }
                      label={
                        formState.subtractFromMargin ? "Expected: Yes" : "Expected: No"
                      }
                    />
                  ) : null}
                </FormGroup>
              </Paper>
            </>
          ) : null}

          {formState.templateKey === "underminimum_required_charge" ? (
            <>
              <TextField
                label="Minimum Quantity"
                type="number"
                value={formState.minimumQuantity}
                onChange={(event) => setField("minimumQuantity", event.target.value)}
                fullWidth
              />
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Required Charge ID"
                  value={formState.requiredChargeId}
                  onChange={(event) =>
                    setField("requiredChargeId", event.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Required Charge Name / Contains"
                  value={formState.requiredChargeName}
                  onChange={(event) =>
                    setField("requiredChargeName", event.target.value)
                  }
                  fullWidth
                />
              </Stack>
            </>
          ) : null}

          {formState.templateKey === "missing_net_price_or_cost" ? (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Charge ID (Optional)"
                  value={formState.missingCostChargeId}
                  onChange={(event) =>
                    setField("missingCostChargeId", event.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Charge Name / Contains (Optional)"
                  value={formState.missingCostChargeName}
                  onChange={(event) =>
                    setField("missingCostChargeName", event.target.value)
                  }
                  fullWidth
                />
              </Stack>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                  Pricing Checks
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={formState.requireNetCost}
                        onChange={(event) =>
                          setField("requireNetCost", event.target.checked)
                        }
                      />
                    )}
                    label="Require Net Cost"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={formState.requireClientPrice}
                        onChange={(event) =>
                          setField("requireClientPrice", event.target.checked)
                        }
                      />
                    )}
                    label="Require Client Price"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={formState.missingCostZeroIsMissing}
                        onChange={(event) =>
                          setField("missingCostZeroIsMissing", event.target.checked)
                        }
                      />
                    )}
                    label="Treat zero as missing"
                  />
                </FormGroup>
              </Paper>
            </>
          ) : null}

          {formState.templateKey === "required_proof" ? (
            <TextField
              label="Proof Requested Field"
              value={formState.proofRequestedField}
              onChange={(event) =>
                setField("proofRequestedField", event.target.value)
              }
              helperText="Field path used to detect whether proof has been requested."
              fullWidth
            />
          ) : null}

          {formState.templateKey === "stitch_threshold" ? (
            <>
              <TextField
                label="Stitch Threshold"
                type="number"
                value={formState.stitchThreshold}
                onChange={(event) => setField("stitchThreshold", event.target.value)}
                fullWidth
              />
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Required Charge ID"
                  value={formState.stitchRequiredChargeId}
                  onChange={(event) =>
                    setField("stitchRequiredChargeId", event.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Required Charge Name / Contains"
                  value={formState.stitchRequiredChargeName}
                  onChange={(event) =>
                    setField("stitchRequiredChargeName", event.target.value)
                  }
                  fullWidth
                />
              </Stack>
              <TextField
                label="Minimum Net Cost (Optional)"
                type="number"
                value={formState.stitchMinimumNetCost}
                onChange={(event) =>
                  setField("stitchMinimumNetCost", event.target.value)
                }
                helperText="Optional alternate pricing check when no specific charge matcher is preferred."
                fullWidth
              />
            </>
          ) : null}

          {formState.templateKey === "dtf_square_inch_pricing" ? (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Method / Process"
                  value={formState.dtfMethod}
                  onChange={(event) => setField("dtfMethod", event.target.value)}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Compare Price Field</InputLabel>
                  <Select
                    label="Compare Price Field"
                    value={formState.dtfPriceField}
                    onChange={(event) => setField("dtfPriceField", event.target.value)}
                  >
                    {DTF_PRICE_FIELD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Width Field"
                  value={formState.dtfWidthField}
                  onChange={(event) =>
                    setField("dtfWidthField", event.target.value)
                  }
                  fullWidth
                />
                <TextField
                  label="Height Field"
                  value={formState.dtfHeightField}
                  onChange={(event) =>
                    setField("dtfHeightField", event.target.value)
                  }
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Expected Rate"
                  type="number"
                  value={formState.dtfExpectedRate}
                  onChange={(event) =>
                    setField("dtfExpectedRate", event.target.value)
                  }
                  helperText="Expected price per square inch."
                  fullWidth
                />
                <TextField
                  label="Charge Name / Contains"
                  value={formState.dtfChargeName}
                  onChange={(event) =>
                    setField("dtfChargeName", event.target.value)
                  }
                  fullWidth
                />
              </Stack>
              <TextField
                label="Charge ID (Optional)"
                value={formState.dtfChargeId}
                onChange={(event) => setField("dtfChargeId", event.target.value)}
                fullWidth
              />
            </>
          ) : null}

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                label="Severity"
                value={formState.severity}
                onChange={(event) => setField("severity", event.target.value)}
              >
                {SEVERITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              sx={{
                ml: 0,
                mr: 0,
                px: 1.5,
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                minHeight: 56,
                justifyContent: "space-between",
              }}
              control={
                <Switch
                  checked={formState.active}
                  onChange={(event) => setField("active", event.target.checked)}
                />
              }
              label="Active"
              labelPlacement="start"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={onSave} variant="contained" disabled={saving}>
          {saving ? <ButtonLoader /> : formState.id ? "Save Changes" : "Create Rule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DecoratorRulesSettingsDialog = ({
  open,
  onClose,
  decorator,
  decoratorsOptions = [],
  currentInstanceId = null,
}) => {
  const decoratorId = decorator?.id ? Number(decorator.id) : null;
  const decoratorName = decorator?.name || "Decorator";
  const [activeTab, setActiveTab] = useState(TAB_INDEX.auditRules);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [error, setError] = useState("");
  const [rules, setRules] = useState([]);
  const [formState, setFormState] = useState(() =>
    buildDefaultFormState({ decoratorId, instanceId: currentInstanceId })
  );

  const canManageInstanceRules = Number.isInteger(Number(currentInstanceId));

  const sortedRules = useMemo(
    () =>
      [...rules].sort((left, right) => {
        const leftTime = new Date(left.updatedAt || left.createdAt || 0).getTime();
        const rightTime = new Date(right.updatedAt || right.createdAt || 0).getTime();
        return rightTime - leftTime;
      }),
    [rules]
  );

  const loadRules = async () => {
    if (!decoratorId) {
      setRules([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [allRulesResponse, instanceRulesResponse, decoratorRulesResponse] =
        await Promise.all([
          getAuditRules(),
          canManageInstanceRules
            ? getAuditRules({ instanceId: currentInstanceId })
            : Promise.resolve({ rules: [] }),
          getAuditRules({ decoratorId }),
        ]);

      const merged = new Map();
      const globalRules = (allRulesResponse?.rules || []).filter(
        (rule) => rule.scopeType === "global"
      );
      const scopedInstanceRules = (instanceRulesResponse?.rules || []).filter(
        (rule) =>
          rule.scopeType === "instance" ||
          (rule.scopeType === "instance_decorator" &&
            Number(rule.decoratorId) === decoratorId)
      );
      const scopedDecoratorRules = (decoratorRulesResponse?.rules || []).filter(
        (rule) =>
          rule.scopeType === "decorator" ||
          (rule.scopeType === "instance_decorator" &&
            (!canManageInstanceRules ||
              Number(rule.instanceId) === Number(currentInstanceId)))
      );

      [...globalRules, ...scopedInstanceRules, ...scopedDecoratorRules].forEach(
        (rule) => {
          if (rule?.id != null) {
            merged.set(rule.id, rule);
          }
        }
      );

      setRules([...merged.values()]);
    } catch (loadError) {
      const message =
        loadError?.response?.data?.message ||
        "Failed to load decorator audit rules.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setActiveTab(TAB_INDEX.auditRules);
    loadRules();
  }, [open, decoratorId, currentInstanceId]);

  useEffect(() => {
    setFormState(
      buildDefaultFormState({ decoratorId, instanceId: currentInstanceId })
    );
  }, [decoratorId, currentInstanceId, open]);

  const openCreateRule = () => {
    setFormState(
      buildDefaultFormState({ decoratorId, instanceId: currentInstanceId })
    );
    setEditorOpen(true);
  };

  const handleCreateRuleType = (templateId) => {
    if (!decoratorId) {
      toast.error("Select a decorator before creating an audit rule.");
      return;
    }

    try {
      setFormState(
        buildQuickTemplateDraft({
          templateId,
          decoratorId,
          instanceId: currentInstanceId,
        })
      );
      setEditorOpen(true);
    } catch (templateError) {
      toast.error(templateError?.message || "Failed to load the selected rule type.");
    }
  };

  const openEditRule = (rule) => {
    setFormState(
      buildFormStateFromRule(rule, {
        decoratorId,
        instanceId: currentInstanceId,
      })
    );
    setEditorOpen(true);
  };

  const handleSaveRule = async () => {
    try {
      setEditorSaving(true);
      const payload = serializeAuditRulePayload(formState);
      if (formState.id) {
        await updateAuditRule(formState.id, payload);
        toast.success("Audit rule updated.");
      } else {
        await createAuditRule(payload);
        toast.success("Audit rule created.");
      }
      setEditorOpen(false);
      await loadRules();
    } catch (saveError) {
      toast.error(
        saveError?.response?.data?.message ||
          saveError?.message ||
          "Failed to save audit rule."
      );
    } finally {
      setEditorSaving(false);
    }
  };

  const handleDeactivateRule = async (ruleId) => {
    if (!ruleId) return;
    try {
      setSaving(true);
      await deactivateAuditRule(ruleId);
      toast.success("Audit rule deactivated.");
      await loadRules();
    } catch (deactivateError) {
      toast.error(
        deactivateError?.response?.data?.message ||
          "Failed to deactivate audit rule."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <SettingsSuggestOutlinedIcon color="primary" />
            <Typography variant="h6">
              {`Rules & Settings: ${decoratorName}`}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#f8fafb", px: 0, py: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_event, value) => setActiveTab(value)}
            sx={{ px: 2, pt: 1.5, backgroundColor: "#fff", borderBottom: "1px solid #e2e8f0" }}
          >
            <Tab label="Audit Rules" />
            <Tab label="Blackout Days" />
            <Tab label="Instance Overrides" />
            <Tab label="Preview Audit" />
          </Tabs>

          {activeTab === TAB_INDEX.auditRules ? (
            <Box sx={{ p: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
                sx={{ mb: 2.5 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Audit Rules
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure decorator-specific audit logic without changing the existing matrix editor.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateRule}
                >
                  Create Rule
                </Button>
              </Stack>

              <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={1.5}
                  sx={{ mb: 1.75 }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Rule Types
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start with a general audit rule type, then customize it for this decorator.
                    </Typography>
                  </Box>
                  {!decoratorId ? (
                    <Chip
                      size="small"
                      label="Select a decorator to enable rule types"
                      variant="outlined"
                    />
                  ) : null}
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                      xl: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {RULE_TYPE_OPTIONS.map((template) => (
                    <Paper
                      key={template.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {template.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                        {template.description}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!decoratorId}
                        onClick={() => handleCreateRuleType(template.id)}
                      >
                        Start Rule
                      </Button>
                    </Paper>
                  ))}
                </Box>

                {COMING_SOON_TEMPLATES.length ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: "#64748b", mb: 1 }}
                    >
                      Coming soon
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {COMING_SOON_TEMPLATES.map((template) => (
                        <Chip
                          key={template.id}
                          label={`${template.label} (Coming soon)`}
                          title={template.description || ""}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                ) : null}
              </Paper>

              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : null}

              {loading ? (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                  <LoadingComponent />
                </Box>
              ) : (
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell>Rule Name</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRules.length ? (
                        sortedRules.map((rule) => (
                          <TableRow key={rule.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {rule.name}
                            </TableCell>
                            <TableCell>{formatTemplateLabel(rule.templateKey)}</TableCell>
                            <TableCell>{formatScopeLabel(rule.scopeType)}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={rule.severity === "fail" ? "error" : "warning"}
                                label={rule.severity === "fail" ? "Fail" : "Warning"}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={rule.active ? "success" : "default"}
                                label={rule.active ? "Active" : "Inactive"}
                                variant={rule.active ? "filled" : "outlined"}
                              />
                            </TableCell>
                            <TableCell>
                              {rule.updatedAt
                                ? new Date(rule.updatedAt).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<EditOutlinedIcon />}
                                  onClick={() => openEditRule(rule)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  startIcon={<DeleteOutlineIcon />}
                                  disabled={saving}
                                  onClick={() => handleDeactivateRule(rule.id)}
                                >
                                  Deactivate
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Box sx={{ py: 6, textAlign: "center", color: "#64748b" }}>
                              <Typography variant="body1" sx={{ mb: 0.5 }}>
                                No audit rules found for this decorator yet.
                              </Typography>
                              <Typography variant="body2">
                                Create a rule to start managing decorator-specific audit behavior.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </Box>
          ) : null}

          {activeTab === TAB_INDEX.blackoutDays ? (
            <PlaceholderTab message="Blackout day rules will be configured here." />
          ) : null}

          {activeTab === TAB_INDEX.instanceOverrides ? (
            <PlaceholderTab message="Instance-specific audit rule overrides will be configured here." />
          ) : null}

          {activeTab === TAB_INDEX.previewAudit ? (
            <PlaceholderTab message="Audit preview will be available after rules are configured." />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <RuleEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveRule}
        saving={editorSaving}
        decoratorsOptions={decoratorsOptions}
        formState={formState}
        setFormState={setFormState}
        currentInstanceId={currentInstanceId}
      />
    </>
  );
};

export default DecoratorRulesSettingsDialog;
